/**
 * AnalyzeView Component - Enhanced
 *
 * Comprehensive COBOL analysis dashboard with:
 * - Multiple visualization tabs (Dependencies, Program Flow, Copybooks, Data Model, Files, Stats)
 * - D3 force-directed graphs, trees, and sankey diagrams
 * - Side panel with summary statistics
 * - Interactive filtering and drill-through
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { ReactFlowProvider } from '@xyflow/react'
import * as d3 from 'd3'
import {
  ArrowLeft, ListTree, LayoutGrid, Network, GitBranch, Database, FileCode,
  Loader2, AlertCircle, BarChart3, Sparkles, FileText, Code2,
  ChevronRight, ExternalLink, Layers, Box, ArrowRightLeft, Activity, Target,
  AlertTriangle, FileBarChart, Download, Scissors, FileArchive,
  BookOpen, BarChart2, CheckCircle2,
} from 'lucide-react'
import { DependencyGraph, CopybookGraph, ComplexityChart, LOCTreemap } from '../graph'
import {
  getGraphDot,
  getCrossReference,
  getProcedureModel,
  getSummary,
  getDataModel,
  getJsonArtifact,
  getJobResults,
  getDeadCodeAnalysis,
  getCopybookChangeImpact,
  getFieldCrossReference,
  getComplexityHeatmap,
} from '../../services/codeAnalysisService'
import { parseDotFile } from '../../utils/dotParser'
import { getAPIBaseUrl } from '../../environment.js'
import '@xyflow/react/dist/style.css'
import './AnalyzeView.css'
import '../guided/GuidedAnalyzeView.css'

/**
 * Tab configuration
 */
const TABS = {
  'overview': {
    label: 'Overview',
    icon: LayoutGrid,
    description: 'Application overview dashboard — composition, structure, data layer, issues',
  },
  'dependencies': {
    label: 'Dependencies',
    icon: Network,
    description: 'Program dependency graph (DOT visualization)',
  },
  'program-flow': {
    label: 'Program Flow',
    icon: GitBranch,
    description: 'Paragraph PERFORM relationships',
  },
  'copybooks': {
    label: 'Copybooks',
    icon: Layers,
    description: 'Copybook and external call dependencies',
  },
  'data-model': {
    label: 'Data Model',
    icon: Database,
    description: 'COBOL to Java data structure mapping',
  },
  'file-io': {
    label: 'File I/O',
    icon: FileText,
    description: 'File operations and data flow',
  },
  'statistics': {
    label: 'Statistics',
    icon: BarChart3,
    description: 'Code metrics and analysis summary',
  },
}

/**
 * DOT Graph view modes
 */
const VIEW_MODES = {
  'high-level': { label: 'High-Level', graphType: 'high_level' },
  'summary': { label: 'Summary', graphType: 'summary' },
  'detailed': { label: 'Detailed', graphType: 'detailed' },
}

/**
 * Professional color palette
 */
const COLORS = {
  primary: '#7aa2f7',
  secondary: '#bb9af7',
  success: '#9ece6a',
  warning: '#e0af68',
  danger: '#f7768e',
  info: '#7dcfff',
  muted: '#565f89',
  nodes: ['#7aa2f7', '#9ece6a', '#e0af68', '#f7768e', '#bb9af7', '#7dcfff', '#ff9e64', '#73daca'],
}

// ── Overview dashboard helpers (ported from GuidedAnalyzeView) ─────────────────

const OV_COMP_TYPES = [
  { key: 'cobol',    label: 'COBOL',     color: '#388bfd' },
  { key: 'copybook', label: 'Copybooks', color: '#a78bfa' },
  { key: 'jcl',      label: 'JCL',       color: '#f59e0b' },
  { key: 'bms',      label: 'BMS',       color: '#22c55e' },
  { key: 'sql',      label: 'SQL',       color: '#f97316' },
  { key: 'unknown',  label: 'Other',     color: '#6b7280' },
]

const OV_ISSUE_META = {
  JCL_EXTERNAL_DATASET:    { label: 'External Dataset',        action: 'Decide whether to migrate or replace each dataset.' },
  DB2_TABLE_UNREFERENCED:  { label: 'Unreferenced DB2 Table',  action: 'Verify table is accessed from another source or exclude from scope.' },
  UNRESOLVED_LOGICAL_FILE: { label: 'Unresolved Logical File', action: 'Check FILE-CONTROL SELECT/ASSIGN values and JCL DD entries.' },
  UNRESOLVED_COPYBOOK:     { label: 'Unresolved Copybook',     action: 'Ensure the copybook is included in the uploaded source.' },
  JCL_UNRESOLVED_PROGRAM:  { label: 'Unresolved JCL Program',  action: 'Include the program in the upload or flag as an external dependency.' },
}

function ovBuildSourceComposition(catalogData) {
  const summary = catalogData?.summary || {}
  const total   = Math.max(Object.values(summary).reduce((a, b) => a + (b || 0), 0), 1)
  return OV_COMP_TYPES
    .map(t => ({ ...t, count: summary[t.key] || 0, pct: Math.round((summary[t.key] || 0) / total * 100) }))
    .filter(t => t.count > 0)
}

function ovBuildCodeSummary(parseData) {
  if (!parseData) return null
  const sum       = parseData.summary || {}
  const xref      = parseData.cross_reference || {}
  const callGraph = xref.call_graph || {}
  const cbUsage   = xref.copybook_usage || {}
  const totalCalls = Object.values(callGraph).reduce((n, arr) => n + (arr?.length || 0), 0)
  return {
    sourceLines:     sum.source_lines  || 0,
    dataItems:       sum.data_items    || 0,
    paragraphs:      sum.paragraphs    || 0,
    filesAnalyzed:   sum.files_analyzed || 0,
    totalCalls,
    uniqueCopybooks: Object.keys(cbUsage).length,
  }
}

function ovBuildTopCopybooks(parseData, limit = 6) {
  if (!parseData) return null
  const cbUsage = parseData.cross_reference?.copybook_usage || {}
  return Object.entries(cbUsage)
    .map(([name, programs]) => ({ name, usedBy: programs?.length || 0 }))
    .sort((a, b) => b.usedBy - a.usedBy)
    .slice(0, limit)
}

function ovBuildExecutionSummary(assetGraph) {
  if (!assetGraph) return null
  const assets  = assetGraph.assets        || []
  const rels    = assetGraph.relationships || []
  const jclJobs = assets.filter(a => a.type === 'JCL_JOB')
  if (jclJobs.length === 0) return null
  return jclJobs.map(job => {
    const execRels = rels.filter(r => r.from === job.id && r.type === 'EXECUTES')
    const ddRels   = rels.filter(r => r.from === job.id && r.type === 'DD_REFERENCES')
    const programs = execRels.map(r => r.metadata?.program || r.to.split(':').pop())
    const steps    = job.metadata?.steps || []
    return { name: job.name, programs, stepCount: steps.length || execRels.length, ddCount: ddRels.length }
  })
}

function ovBuildDataLayer(assetGraph) {
  if (!assetGraph) return null
  const assets = assetGraph.assets || []
  const vsam   = assets.filter(a => a.type === 'VSAM_DATASET' || a.type === 'DATA_FILE')
  const db2    = assets.filter(a => a.type === 'DB2_TABLE')
  const dclgen = assets.filter(a => a.type === 'DB2_DCLGEN')
  if (vsam.length === 0 && db2.length === 0 && dclgen.length === 0) return null
  return { vsam, db2, dclgen }
}

function ovBuildNarrative(assetGraph, parseData, catalogData, topCB) {
  const sentences = []
  const agSum  = assetGraph?.summary || {}
  const catSum = catalogData?.summary || {}
  const programs  = agSum.asset_counts?.COBOL_PROGRAM || catSum.cobol    || 0
  const cbs       = agSum.asset_counts?.COPYBOOK       || catSum.copybook || 0
  const jclJobs   = agSum.jcl_job_count || catSum.jcl || 0
  const dataFiles = (agSum.vsam_dataset_count || 0) + (agSum.data_file_count || 0)
  const db2Tables = agSum.db2_table_count || 0
  if (programs > 0 || cbs > 0) {
    const parts = []
    if (programs > 0)   parts.push(`${programs} COBOL program${programs !== 1 ? 's' : ''}`)
    if (cbs > 0)        parts.push(`${cbs} shared copybook${cbs !== 1 ? 's' : ''}`)
    if (jclJobs > 0)    parts.push(`${jclJobs} JCL job${jclJobs !== 1 ? 's' : ''}`)
    if (dataFiles > 0)  parts.push(`${dataFiles} data file${dataFiles !== 1 ? 's' : ''}`)
    if (db2Tables > 0)  parts.push(`${db2Tables} DB2 table${db2Tables !== 1 ? 's' : ''}`)
    sentences.push(`This application contains ${parts.join(', ')}.`)
  }
  if (assetGraph) {
    const jclAssets = (assetGraph.assets || []).filter(a => a.type === 'JCL_JOB')
    const rels      = assetGraph.relationships || []
    if (jclAssets.length > 0) {
      const job      = jclAssets[0]
      const execRels = rels.filter(r => r.from === job.id && r.type === 'EXECUTES')
      const progs    = execRels.map(r => r.metadata?.program || r.to.split(':').pop())
      const nSteps   = job.metadata?.steps?.length || execRels.length
      if (progs.length > 0)
        sentences.push(`The primary execution path is ${job.name} → ${progs.join(', ')} via ${nSteps} step${nSteps !== 1 ? 's' : ''}.`)
    } else if (parseData) {
      const cg  = parseData.cross_reference?.call_graph || {}
      const top = Object.entries(cg).map(([prog, calls]) => ({ prog, count: calls?.length || 0 })).sort((a, b) => b.count - a.count)[0]
      if (top && top.count > 0)
        sentences.push(`${top.prog} is the most active program, making ${top.count} external call${top.count !== 1 ? 's' : ''}.`)
    }
  }
  if (topCB && topCB.length > 0 && topCB[0].usedBy > 1) {
    const top = topCB[0]
    sentences.push(`'${top.name}' is the most reused shared structure, referenced by ${top.usedBy} program${top.usedBy !== 1 ? 's' : ''}.`)
  }
  if (parseData) {
    const psum      = parseData.summary || {}
    const srcLines  = psum.source_lines || 0
    const dataItems = psum.data_items   || 0
    const derivedParas = Object.values(parseData.programs || {})
      .reduce((n, p) => n + ((p.paragraphs || []).length), 0)
    const parts = []
    if (srcLines    > 0) parts.push(`${srcLines.toLocaleString()} source lines`)
    if (derivedParas > 0) parts.push(`${derivedParas} paragraphs`)
    if (dataItems   > 0) parts.push(`${dataItems.toLocaleString()} data items`)
    if (parts.length > 0)
      sentences.push(`Code Analysis found ${parts.join(', ')} mapped to Java types.`)
  }
  const issueCount  = agSum.total_issues  || 0
  const issueCounts = agSum.issue_counts  || {}
  if (issueCount > 0) {
    const extDs    = issueCounts.JCL_EXTERNAL_DATASET   || 0
    const unrefDb2 = issueCounts.DB2_TABLE_UNREFERENCED || 0
    const parts    = []
    if (extDs    > 0) parts.push(`${extDs} external dataset${extDs !== 1 ? 's' : ''} requiring migration decisions`)
    if (unrefDb2 > 0) parts.push(`${unrefDb2} DB2 table${unrefDb2 !== 1 ? 's' : ''} with no detected SQL references`)
    const other = issueCount - extDs - unrefDb2
    if (other > 0) parts.push(`${other} other item${other !== 1 ? 's' : ''} for review`)
    if (parts.length > 0) sentences.push(`Review required: ${parts.join('; ')}.`)
  } else if (assetGraph && issueCount === 0) {
    sentences.push('No system issues detected — all assets and references resolve within the application boundary.')
  }
  return sentences
}

/**
 * Main AnalyzeView component
 */
function AnalyzeViewInner({
  onBack,
  phaseName = 'Analyze',
  analysisContext = null,
}) {
  const [activeTab, setActiveTab] = useState('overview')
  const [viewMode, setViewMode] = useState('high-level')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Data state
  const [graphData, setGraphData] = useState(null)
  const [crossReference, setCrossReference] = useState(null)
  const [procedureModel, setProcedureModel] = useState(null)
  const [summary, setSummary] = useState(null)
  const [lineInventory, setLineInventory] = useState(null)
  const [dataModel, setDataModel] = useState(null)
  const [fileModel, setFileModel] = useState(null)
  const [comprehensiveData, setComprehensiveData] = useState(null)

  // Overview tab state — asset graph + catalog from ingest
  const [assetGraph, setAssetGraph] = useState(null)
  const [catalogData, setCatalogData] = useState(null)
  const [intelligenceMetrics, setIntelligenceMetrics] = useState(null)
  const [overviewLoading, setOverviewLoading] = useState(false)

  // Tier 1 enhancement state — graph analysis queries
  const [deadCode, setDeadCode] = useState(null)
  const [copybookImpact, setCopybookImpact] = useState(null)
  const [fieldXref, setFieldXref] = useState(null)
  const [complexityHeatmap, setComplexityHeatmap] = useState(null)

  const jobContext = analysisContext

  // Log context for debugging
  useEffect(() => {
    console.log(' [ANALYZE_VIEW] Received context:', {
      hasContext: !!jobContext,
      jobId: jobContext?.jobId,
      baseName: jobContext?.baseName,
      mainProgram: jobContext?.mainProgram,
      artifactsPath: jobContext?.artifactsPath,
      hasJobs: !!jobContext?.jobs,
      jobKeys: jobContext?.jobs ? Object.keys(jobContext.jobs) : []
    })

    if (jobContext && (!jobContext.jobId || !jobContext.baseName)) {
      console.error(' [ANALYZE_VIEW] ERROR: Context is incomplete!', {
        jobId: jobContext.jobId,
        baseName: jobContext.baseName,
        fullContext: jobContext
      })
      setError('Missing required context (jobId or baseName). Cannot load analysis data.')
    }
  }, [jobContext])

  /**
   * Fetch all analysis data
   */
  const fetchAllData = useCallback(async () => {
    if (!jobContext?.jobId) {
      console.warn(' [ANALYZE_VIEW] Skipping fetch - missing jobId')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Step 1: Resolve the authoritative baseName from the job record.
      // ReportsView passes selectedAppName.toLowerCase() which may not match the
      // actual COBOL program name used as the artifact filename prefix.
      // The /results endpoint returns base_name from output_data — use that when available.
      let baseName = jobContext.baseName
      try {
        const jobResults = await getJobResults(jobContext.jobId)
        if (jobResults?.base_name) {
          baseName = jobResults.base_name
          console.log(` [ANALYZE_VIEW] Resolved authoritative baseName: "${baseName}" (context had: "${jobContext.baseName}")`)
        }
      } catch (e) {
        console.debug(' [ANALYZE_VIEW] Could not resolve baseName from /results, using context value:', baseName)
      }

      if (!baseName) {
        console.warn(' [ANALYZE_VIEW] No baseName available — optional artifacts will be skipped')
      }

      console.log(' [ANALYZE_VIEW] Fetching data with:', { jobId: jobContext.jobId, baseName })

      // Step 2: Fetch all artifacts. Required artifacts (crossRef, summary from
      // comprehensive_parse_results) are fetched unconditionally. Optional
      // baseName-prefixed artifacts are only fetched when baseName is known.
      const fetches = [
        getCrossReference(jobContext.jobId),                             // 0 — required
        getSummary(jobContext.jobId, baseName),                          // 1 — required
        getJsonArtifact(jobContext.jobId, 'comprehensive_parse_results.json'), // 2 — required
        baseName
          ? getProcedureModel(jobContext.jobId, baseName)
          : Promise.reject(new Error('baseName unknown')),               // 3 — optional
        baseName
          ? getJsonArtifact(jobContext.jobId, `${baseName}_line_inventory.json`)
          : Promise.reject(new Error('baseName unknown')),               // 4 — optional
        baseName
          ? getDataModel(jobContext.jobId, baseName)
          : Promise.reject(new Error('baseName unknown')),               // 5 — optional
        baseName
          ? getJsonArtifact(jobContext.jobId, `${baseName}_file_model.json`)
          : Promise.reject(new Error('baseName unknown')),               // 6 — optional
      ]

      const [crossRef, summaryData, comprehensiveResults,
             procModel, lineInvData, dataModelData, fileModelData] = await Promise.allSettled(fetches)

      // Required artifacts
      if (crossRef.status === 'fulfilled') setCrossReference(crossRef.value)
      if (summaryData.status === 'fulfilled') setSummary(summaryData.value)
      if (comprehensiveResults.status === 'fulfilled') setComprehensiveData(comprehensiveResults.value)

      // Optional artifacts — log at debug level only, never error
      if (procModel.status === 'fulfilled') {
        setProcedureModel(procModel.value)
      } else {
        console.debug(' [ANALYZE_VIEW] procedure_model not available (optional):', procModel.reason?.message)
      }
      if (lineInvData.status === 'fulfilled') {
        setLineInventory(lineInvData.value)
      } else {
        console.debug(' [ANALYZE_VIEW] line_inventory not available (optional):', lineInvData.reason?.message)
      }
      if (dataModelData.status === 'fulfilled') {
        setDataModel(dataModelData.value)
      } else {
        console.debug(' [ANALYZE_VIEW] data_model not available (optional):', dataModelData.reason?.message)
      }
      if (fileModelData.status === 'fulfilled') {
        setFileModel(fileModelData.value)
      } else {
        console.debug(' [ANALYZE_VIEW] file_model not available (optional):', fileModelData.reason?.message)
      }

    } catch (err) {
      console.error('Failed to fetch analysis data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [jobContext?.jobId, jobContext?.baseName])

  /**
   * Fetch DOT graph data
   */
  const fetchGraphData = useCallback(async () => {
    if (!jobContext?.jobId || !jobContext?.baseName) return

    setLoading(true)
    try {
      const modeConfig = VIEW_MODES[viewMode]
      const dotContent = await getGraphDot(jobContext.jobId, jobContext.baseName, modeConfig.graphType)
      if (dotContent) {
        setGraphData(parseDotFile(dotContent))
      }
    } catch (err) {
      console.error('Failed to fetch graph:', err)
    } finally {
      setLoading(false)
    }
  }, [jobContext?.jobId, jobContext?.baseName, viewMode])

  /**
   * Fetch Overview tab data — asset graph + catalog (via ingest-link)
   * Completely independent of the existing fetchAllData flow.
   * Both fetches are optional: if either fails, the Overview tab degrades gracefully.
   */
  const fetchOverviewData = useCallback(async () => {
    if (!jobContext?.jobId) return
    setOverviewLoading(true)

    const apiBase = window.__MODERNIZEIT_API_URL__ || getAPIBaseUrl()
    const token   = localStorage.getItem('modernizeit_token') || ''
    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    const fetchJson = (url) =>
      fetch(url, { headers }).then(r => r.ok ? r.json() : null).catch(() => null)

    // 1. Asset graph — generated by CA executor Step 7.5 for all run types
    const agUrl      = `${apiBase}/codeanalysis/${jobContext.jobId}/results/json/guided_analyze_asset_graph.json`
    const metricsUrl = `${apiBase}/codeanalysis/${jobContext.jobId}/results/json/intelligence_metrics.json`
    const [ag, metrics] = await Promise.all([fetchJson(agUrl), fetchJson(metricsUrl)])
    if (ag)      setAssetGraph(ag)
    if (metrics) setIntelligenceMetrics(metrics)

    // 1b. Tier 1 enhancement analysis — graph queries served as JSON artifacts
    const enhancementFetches = [
      fetchJson(`${apiBase}/codeanalysis/${jobContext.jobId}/results/json/dead_code_analysis.json`),
      fetchJson(`${apiBase}/codeanalysis/${jobContext.jobId}/results/json/copybook_change_impact.json`),
      fetchJson(`${apiBase}/codeanalysis/${jobContext.jobId}/results/json/field_cross_reference.json`),
      fetchJson(`${apiBase}/codeanalysis/${jobContext.jobId}/results/json/complexity_heatmap.json`),
    ]
    const [dc, ci, fx, ch] = await Promise.all(enhancementFetches)
    if (dc) setDeadCode(dc)
    if (ci) setCopybookImpact(ci)
    if (fx) setFieldXref(fx)
    if (ch) setComplexityHeatmap(ch)

    // 2. Classified catalog — behind ingest job ID, recover via CA ingest-link
    try {
      const linkRes = await fetch(`${apiBase}/codeanalysis/${jobContext.jobId}/ingest-link`, { headers })
      if (linkRes.ok) {
        const linkData = await linkRes.json()
        const ingestId = linkData?.ingest_job_id
        if (ingestId) {
          const ingestRes = await fetch(`${apiBase}/ingest/results/${ingestId}`, { headers })
          if (ingestRes.ok) {
            const ingestData = await ingestRes.json()
            const catalog = ingestData?.classified_catalog
            if (catalog) setCatalogData(catalog)
          }
        }
      }
    } catch (e) {
      console.debug('[AnalyzeView] Catalog recovery via ingest-link skipped:', e.message)
    }

    setOverviewLoading(false)
  }, [jobContext?.jobId])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchOverviewData()
  }, [fetchOverviewData])

  useEffect(() => {
    if (activeTab === 'dependencies') {
      fetchGraphData()
    }
  }, [activeTab, fetchGraphData])

  // Computed stats — only block if we have no primary data at all.
  // procedureModel is optional; derive paragraph count from crossReference if unavailable.
  const stats = useMemo(() => {
    // Need at least summary OR crossReference to render meaningful stats
    if (!summary && !crossReference) return null

    const copybooks = crossReference?.copybook_usage
      ? Object.keys(crossReference.copybook_usage).length
      : 0
    const externalCalls = crossReference?.call_graph?.[`${jobContext?.mainProgram}`]?.length || 0

    // Prefer procedureModel for paragraph count; fall back to crossReference paragraph list
    const paragraphs = procedureModel?.paragraphs?.length
      ?? crossReference?.paragraphs?.length
      ?? crossReference?.procedure_division?.paragraphs?.length
      ?? 0

    const totalLines = lineInventory?.source_line_count || summary?.source_line_count || 0

    return {
      totalLines,
      paragraphs,
      copybooks,
      externalCalls,
      classifications: summary?.classifications || {},
    }
  }, [summary, lineInventory, crossReference, procedureModel, jobContext])

  const handleRefresh = () => {
    fetchAllData()
    if (activeTab === 'dependencies') fetchGraphData()
  }

  return (
    <div className="analyze-view enhanced">
      {/* Header */}
      <div className="analyze-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack}>
            <ArrowLeft size={18} />
            <span>Back to Workflow</span>
          </button>
          <div className="view-title">
            <h2>Code Analysis: {jobContext?.mainProgram || 'COBOL Program'}</h2>
            <span className="view-subtitle">Interactive Analysis Dashboard</span>
          </div>
        </div>

        <div className="header-center">
          <div className="tab-switcher">
            {Object.entries(TABS).map(([tab, config]) => {
              const Icon = config.icon
              return (
                <button
                  key={tab}
                  className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                  title={config.description}
                >
                  <Icon size={16} />
                  <span className="tab-label">{config.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="header-right" />
      </div>

      {/* Main content with side panel */}
      <div className="analyze-content">
        {!jobContext && !loading && (
          <div className="no-data-state">
            <AlertCircle size={48} />
            <h3>No Analysis Data Available</h3>
            <p>Run the Code Analysis node in your workflow first.</p>
            <button className="back-to-workflow-btn" onClick={onBack}>
              <ArrowLeft size={18} /> Back to Workflow
            </button>
          </div>
        )}

        {jobContext && (
          <div className="dashboard-layout">
            {/* Main chart area */}
            <div className="chart-container">
              {activeTab === 'overview' && (
                <OverviewTab
                  assetGraph={assetGraph}
                  catalogData={catalogData}
                  parseData={comprehensiveData}
                  intelligenceMetrics={intelligenceMetrics}
                  deadCode={deadCode}
                  loading={overviewLoading}
                  mainProgram={jobContext?.mainProgram}
                />
              )}
              {activeTab === 'dependencies' && (
                <DependenciesTab
                  graphData={graphData}
                  crossReference={crossReference}
                  mainProgram={jobContext?.mainProgram}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  loading={loading}
                  error={error}
                />
              )}
              {activeTab === 'program-flow' && (
                <ProgramFlowTab data={procedureModel} loading={loading} />
              )}
              {activeTab === 'copybooks' && (
                <CopybooksTab data={crossReference} copybookImpact={copybookImpact} mainProgram={jobContext?.mainProgram} loading={loading} />
              )}
              {activeTab === 'data-model' && (
                <DataModelTab data={dataModel} fieldXref={fieldXref} loading={loading} />
              )}
              {activeTab === 'file-io' && (
                <FileIOTab data={fileModel} loading={loading} />
              )}
              {activeTab === 'statistics' && (
                <StatisticsTab summary={summary} lineInventory={lineInventory} crossRef={crossReference} procModel={procedureModel} comprehensiveData={comprehensiveData} complexityHeatmap={complexityHeatmap} loading={loading} />
              )}
            </div>

            {/* Side panel */}
            <div className="side-panel">
              <div className="side-panel-scroll">
                <SummaryPanel stats={stats} loading={loading} />
                <ArtifactActionsPanel
                  caJobId={jobContext.jobId}
                  crJobId={jobContext.jobs?.coderefactor_job_id}
                  baseName={jobContext.baseName}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Overview KPI card ─────────────────────────────────────────────────────────
function OvCard({ label, value, pending = false }) {
  return (
    <div className="ga-dash-card">
      <span className="ga-dash-card-value">
        {pending
          ? <Loader2 size={14} className="ga-load-spinner" />
          : (value !== null && value !== undefined)
            ? (typeof value === 'number' ? value.toLocaleString() : value)
            : '—'
        }
      </span>
      <span className="ga-dash-card-label">{label}</span>
    </div>
  )
}

// ── Overview stat cell ────────────────────────────────────────────────────────
function OvStat({ label, value }) {
  return (
    <div className="ga-dash-stat">
      <span className="ga-dash-stat-value">{value}</span>
      <span className="ga-dash-stat-label">{label}</span>
    </div>
  )
}

/**
 * Overview Tab — Application dashboard.
 *
 * Data sources (all fetched independently of the existing tab artifacts):
 *   guided_analyze_asset_graph.json  — KPI counts, JCL entry points, data layer, issues
 *   classified_catalog.json          — source composition breakdown
 *   comprehensive_parse_results.json — copybook usage, code depth (already fetched)
 *   intelligence_metrics.json        — risk tier distribution
 *
 * All sections degrade gracefully when their source is unavailable.
 */
function OverviewTab({ assetGraph, catalogData, parseData, intelligenceMetrics, deadCode, loading, mainProgram }) {
  const agSum    = assetGraph?.summary       || {}
  const agAssets = assetGraph?.assets        || []
  const agIssues = assetGraph?.issues        || []
  const hasGraph = !!assetGraph

  // KPI strip — asset graph primary, catalog fallback
  const kpiTotalAssets = hasGraph ? (agSum.total_assets || 0) : 0
  const kpiPrograms    = hasGraph
    ? (agSum.asset_counts?.COBOL_PROGRAM || 0)
    : (catalogData?.summary?.cobol || 0)
  const kpiCopybooks   = hasGraph
    ? (agSum.asset_counts?.COPYBOOK || 0)
    : (catalogData?.summary?.copybook || 0)
  const kpiJclJobs   = agSum.jcl_job_count   || 0
  const kpiDataFiles = (agSum.vsam_dataset_count || 0) + (agSum.data_file_count || 0)
  const kpiDb2Tables = agSum.db2_table_count  || 0
  const kpiRelships  = agSum.total_relationships || 0
  const kpiIssues    = agSum.total_issues     || 0

  // Section data
  const sourceComp  = ovBuildSourceComposition(catalogData)
  const codeSum     = ovBuildCodeSummary(parseData)
  const topCB       = ovBuildTopCopybooks(parseData)
  const execSummary = ovBuildExecutionSummary(assetGraph)
  const dataLayer   = ovBuildDataLayer(assetGraph)
  const narrative   = ovBuildNarrative(assetGraph, parseData, catalogData, topCB)

  // Issue groups
  const issueGroups = {}
  agIssues.forEach(issue => {
    const t = issue.type || issue.issue_type || 'UNKNOWN'
    if (!issueGroups[t]) issueGroups[t] = []
    issueGroups[t].push(issue)
  })

  if (loading && !hasGraph && !catalogData) {
    return (
      <div className="tab-content overview-tab">
        <div className="loading-state">
          <Loader2 size={32} className="spin" />
          <span>Loading overview data…</span>
        </div>
      </div>
    )
  }

  return (
    <div className="tab-content overview-tab" style={{ overflowY: 'auto', padding: '20px' }}>

      {/* ── KPI Strip ──────────────────────────────────────────────── */}
      <div className="ga-dash-cards">
        <OvCard label="Total Assets"   value={kpiTotalAssets} />
        <OvCard label="COBOL Programs" value={kpiPrograms} />
        <OvCard label="Copybooks"      value={kpiCopybooks} />
        {hasGraph && <OvCard label="JCL Jobs"      value={kpiJclJobs} />}
        {hasGraph && kpiDataFiles > 0 && <OvCard label="Data Files"    value={kpiDataFiles} />}
        {hasGraph && <OvCard label="DB2 Tables"    value={kpiDb2Tables} />}
        {hasGraph && <OvCard label="Relationships" value={kpiRelships} />}
        <OvCard label="Issues"       value={hasGraph ? kpiIssues : null} pending={loading && !hasGraph} />
        <OvCard label="Source Lines" value={codeSum?.sourceLines ?? null} pending={loading && !codeSum} />
        <OvCard label="Data Items"   value={codeSum?.dataItems   ?? null} pending={loading && !codeSum} />
      </div>

      {/* ── Source Composition + Execution Entry Points ───────────── */}
      <div className="ga-dash-row">

        {/* Source Composition — from classified_catalog */}
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <BarChart2 size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">Source Composition</span>
            <span className="ga-dash-source-tag">ingest</span>
          </div>
          <div className="ga-comp-list">
            {sourceComp.length > 0
              ? sourceComp.map(item => (
                  <div key={item.key} className="ga-comp-row">
                    <span className="ga-comp-label" style={{ color: item.color }}>{item.label}</span>
                    <div className="ga-comp-bar-track">
                      <div className="ga-comp-bar-fill"
                        style={{ width: `${Math.max(item.pct, 2)}%`, backgroundColor: item.color }} />
                    </div>
                    <span className="ga-comp-pct">{item.pct}%</span>
                    <span className="ga-comp-count">{item.count}</span>
                  </div>
                ))
              : <p className="ga-dash-empty">
                  {loading ? 'Loading composition…' : 'No file classification data available.'}
                </p>
            }
          </div>
        </div>

        {/* Execution Entry Points — from asset graph JCL / code depth fallback */}
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <GitBranch size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">
              {execSummary ? 'Execution Entry Points' : 'Code Depth'}
            </span>
            <span className={`ga-dash-source-tag${!hasGraph && !codeSum ? ' ga-dash-source-tag--pending' : ''}`}>
              {execSummary ? 'asset graph · JCL' : hasGraph ? 'asset graph' : codeSum ? 'code analysis' : 'pending'}
            </span>
          </div>
          {loading && !hasGraph && !codeSum ? (
            <div className="ga-dash-pending">
              <Loader2 size={15} className="ga-load-spinner" /><span>Loading…</span>
            </div>
          ) : execSummary ? (
            <div className="ga-dash-exec-list">
              {execSummary.map(entry => (
                <div key={entry.name} className="ga-dash-exec-entry">
                  <div className="ga-dash-exec-header">
                    <span className="ga-dash-exec-job">{entry.name}</span>
                    <span className="ga-dash-exec-meta">
                      {entry.stepCount} step{entry.stepCount !== 1 ? 's' : ''} · {entry.ddCount} DD ref{entry.ddCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {entry.programs.length > 0 && (
                    <div className="ga-dash-exec-targets">
                      <span className="ga-dash-exec-arrow">executes</span>
                      <span className="ga-dash-exec-progs">{entry.programs.join(', ')}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : codeSum ? (
            <div className="ga-dash-stat-grid">
              <OvStat label="Source Lines"    value={codeSum.sourceLines.toLocaleString()} />
              <OvStat label="Paragraphs"      value={codeSum.paragraphs.toLocaleString()} />
              <OvStat label="Data Items"      value={codeSum.dataItems.toLocaleString()} />
              <OvStat label="External Calls"  value={codeSum.totalCalls.toLocaleString()} />
              {codeSum.uniqueCopybooks > 0 && (
                <OvStat label="Unique Copybooks" value={codeSum.uniqueCopybooks.toString()} />
              )}
            </div>
          ) : (
            <div className="ga-dash-unavail">
              <p className="ga-dash-unavail-title">No execution data</p>
              <p className="ga-dash-unavail-hint">Run the full pipeline to see JCL entry points and program call graphs.</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Top Shared Structures + Data Layer ───────────────────── */}
      <div className="ga-dash-row">

        {/* Top Copybooks — from comprehensive_parse_results */}
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <BookOpen size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">Top Shared Structures</span>
            <span className={`ga-dash-source-tag${!codeSum ? ' ga-dash-source-tag--pending' : ''}`}>
              {codeSum ? 'code analysis' : 'pending'}
            </span>
          </div>
          {loading && !codeSum ? (
            <div className="ga-dash-pending">
              <Loader2 size={15} className="ga-load-spinner" /><span>Loading…</span>
            </div>
          ) : topCB && topCB.length > 0 ? (
            <div className="ga-dash-cb-list">
              {topCB.map(cb => (
                <div key={cb.name} className="ga-dash-cb-row">
                  <span className="ga-dash-cb-name">{cb.name}</span>
                  <span className="ga-dash-cb-bar-wrap">
                    <span className="ga-dash-cb-bar" style={{ width: `${Math.min(cb.usedBy * 20, 100)}%` }} />
                  </span>
                  <span className="ga-dash-cb-count">{cb.usedBy} program{cb.usedBy !== 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="ga-dash-unavail">
              <p className="ga-dash-unavail-title">
                {codeSum ? 'No copybook usage data found' : 'Code Analysis not yet run'}
              </p>
              <p className="ga-dash-unavail-hint">
                {codeSum ? 'No COPY statements detected.' : 'Run the full pipeline to see copybook usage analysis.'}
              </p>
            </div>
          )}
        </div>

        {/* Data Layer — from asset graph */}
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <Database size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">Data Layer</span>
            <span className={`ga-dash-source-tag${!hasGraph ? ' ga-dash-source-tag--pending' : ''}`}>
              {hasGraph ? 'asset graph' : 'pending'}
            </span>
          </div>
          {loading && !hasGraph ? (
            <div className="ga-dash-pending">
              <Loader2 size={15} className="ga-load-spinner" /><span>Loading…</span>
            </div>
          ) : dataLayer ? (
            <div className="ga-dash-data-groups">
              {dataLayer.vsam.length > 0 && (
                <div className="ga-dash-data-group">
                  <span className="ga-dash-data-group-label">VSAM / Data Files</span>
                  <div className="ga-dash-data-chips">
                    {dataLayer.vsam.map(a => (
                      <span key={a.id} className="ga-dash-data-chip ga-dash-data-chip--vsam">{a.name}</span>
                    ))}
                  </div>
                </div>
              )}
              {dataLayer.db2.length > 0 && (
                <div className="ga-dash-data-group">
                  <span className="ga-dash-data-group-label">DB2 Tables</span>
                  <div className="ga-dash-data-chips">
                    {dataLayer.db2.map(a => (
                      <span key={a.id} className="ga-dash-data-chip ga-dash-data-chip--db2">{a.name}</span>
                    ))}
                  </div>
                </div>
              )}
              {dataLayer.dclgen.length > 0 && (
                <div className="ga-dash-data-group">
                  <span className="ga-dash-data-group-label">DB2 DCLGENs</span>
                  <div className="ga-dash-data-chips">
                    {dataLayer.dclgen.map(a => (
                      <span key={a.id} className="ga-dash-data-chip ga-dash-data-chip--dclgen">{a.name}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="ga-dash-unavail">
              <p className="ga-dash-unavail-title">
                {hasGraph ? 'No data assets detected' : 'Asset graph not available'}
              </p>
              <p className="ga-dash-unavail-hint">
                {hasGraph
                  ? 'No VSAM datasets or DB2 tables were resolved.'
                  : 'Run the full pipeline to discover VSAM datasets and DB2 tables.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Review Items ─────────────────────────────────────────── */}
      {(hasGraph || (loading && !hasGraph)) && (
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <AlertTriangle size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">Review Items</span>
            <span className={`ga-dash-source-tag${kpiIssues > 0 ? ' ga-dash-source-tag--warn' : ''}`}>
              {loading && !hasGraph
                ? 'loading'
                : kpiIssues > 0
                  ? `${kpiIssues} item${kpiIssues !== 1 ? 's' : ''} · asset graph`
                  : 'none found'}
            </span>
          </div>
          {loading && !hasGraph ? (
            <div className="ga-dash-pending">
              <Loader2 size={15} className="ga-load-spinner" /><span>Loading…</span>
            </div>
          ) : kpiIssues === 0 ? (
            <div className="ga-dash-ok">
              <CheckCircle2 size={15} className="ga-dash-ok-icon" />
              <span>No system issues detected — all assets and references resolved.</span>
            </div>
          ) : (
            <div className="ga-dash-review-groups">
              {Object.entries(issueGroups).map(([issueType, issues]) => {
                const meta = OV_ISSUE_META[issueType]
                return (
                  <div key={issueType} className="ga-dash-review-group">
                    <div className="ga-dash-review-group-hdr">
                      <AlertTriangle size={12} className="ga-dash-issue-icon" />
                      <span className="ga-dash-review-group-label">
                        {meta?.label || issueType.replace(/_/g, ' ')}
                      </span>
                      <span className="ga-dash-issue-count">{issues.length}</span>
                    </div>
                    <div className="ga-dash-review-items">
                      {issues.slice(0, 5).map((issue, i) => (
                        <div key={i} className="ga-dash-review-item">
                          <span className="ga-dash-review-asset">
                            {issue.asset_id?.split(':').pop() || issue.asset_id}
                          </span>
                          <span className="ga-dash-review-action">
                            {issue.detail || meta?.action || ''}
                          </span>
                        </div>
                      ))}
                      {issues.length > 5 && (
                        <div className="ga-dash-review-more">+{issues.length - 5} more</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Dead Code Detection ──────────────────────────────────── */}
      {deadCode?.records?.length > 0 && (() => {
        const deadParas = deadCode.records.filter(r => !r.is_entry_point)
        const entryParas = deadCode.records.filter(r => r.is_entry_point)
        const totalStmts = deadParas.reduce((s, r) => s + (r.statement_count || 0), 0)
        return deadParas.length > 0 ? (
          <div className="ga-dash-panel">
            <div className="ga-dash-panel-header">
              <AlertTriangle size={14} className="ga-dash-panel-icon" />
              <span className="ga-dash-panel-title">Dead Code Detection</span>
              <span className="ga-dash-source-tag ga-dash-source-tag--warn">
                {deadParas.length} paragraph{deadParas.length !== 1 ? 's' : ''} · {totalStmts} statements
              </span>
            </div>
            <div className="ga-dash-review-groups">
              <div className="ga-dash-review-group">
                <div className="ga-dash-review-group-hdr">
                  <AlertTriangle size={12} className="ga-dash-issue-icon" />
                  <span className="ga-dash-review-group-label">Never PERFORMed</span>
                  <span className="ga-dash-issue-count">{deadParas.length}</span>
                </div>
                <div className="ga-dash-review-items">
                  {deadParas.slice(0, 8).map((r, i) => (
                    <div key={i} className="ga-dash-review-item">
                      <span className="ga-dash-review-asset">{r.paragraph}</span>
                      <span className="ga-dash-review-action">
                        {r.statement_count || 0} stmts · lines {r.start_line || '?'}-{r.end_line || '?'}
                      </span>
                    </div>
                  ))}
                  {deadParas.length > 8 && (
                    <div className="ga-dash-review-more">+{deadParas.length - 8} more</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null
      })()}

      {/* ── Risk Intelligence ────────────────────────────────────── */}
      {intelligenceMetrics && (
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <AlertTriangle size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">Risk Intelligence</span>
            <span className="ga-dash-source-tag">intelligence · Phase 1</span>
          </div>
          {(() => {
            const ps    = intelligenceMetrics.project_summary || {}
            const total = ps.total_programs || 1
            const hi    = ps.high_risk_count   || 0
            const med   = ps.medium_risk_count  || 0
            const lo    = ps.low_risk_count     || 0
            const hiPct  = Math.round(hi  / total * 100)
            const medPct = Math.round(med / total * 100)
            const loPct  = 100 - hiPct - medPct
            const progs  = intelligenceMetrics.programs || {}
            const top3   = Object.entries(progs)
              .sort((a, b) => b[1].risk_score - a[1].risk_score)
              .slice(0, 3)
            return (
              <div className="ga-dash-risk-intel">
                <div className="ga-dash-risk-dist">
                  <div className="ga-dash-risk-dist-bar">
                    {hiPct  > 0 && <div className="ga-dash-risk-dist-seg ga-dash-risk-dist-seg--high"   style={{ width: `${hiPct}%`  }} />}
                    {medPct > 0 && <div className="ga-dash-risk-dist-seg ga-dash-risk-dist-seg--medium" style={{ width: `${medPct}%` }} />}
                    {loPct  > 0 && <div className="ga-dash-risk-dist-seg ga-dash-risk-dist-seg--low"    style={{ width: `${loPct}%`  }} />}
                  </div>
                  <div className="ga-dash-risk-dist-labels">
                    <span className="ga-dash-risk-dist-lbl ga-dash-risk-dist-lbl--high">{hi} High</span>
                    <span className="ga-dash-risk-dist-lbl ga-dash-risk-dist-lbl--medium">{med} Medium</span>
                    <span className="ga-dash-risk-dist-lbl ga-dash-risk-dist-lbl--low">{lo} Low</span>
                    <span className="ga-dash-risk-dist-total">{total} program{total !== 1 ? 's' : ''} scored</span>
                  </div>
                </div>
                {top3.length > 0 && (
                  <div className="ga-dash-risk-top">
                    <span className="ga-dash-risk-top-label">Highest Risk</span>
                    {top3.map(([stem, m], idx) => (
                      <div key={stem} className="ga-dash-risk-top-row">
                        <span className="ga-dash-risk-top-rank">{idx + 1}</span>
                        <span className="ga-dash-risk-top-name">{stem}</span>
                        <div className="ga-dash-risk-top-bar-wrap">
                          <div
                            className={`ga-dash-risk-top-bar ga-dash-risk-top-bar--${m.risk_tier}`}
                            style={{ width: `${m.risk_score}%` }}
                          />
                        </div>
                        <span className={`ga-risk-badge ga-risk-badge--${m.risk_tier}`}>{m.risk_score}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="ga-dash-risk-intel-note">
                  Score = Fan-Out (60%) + Instability (40%) — relative to this project.
                </p>
              </div>
            )
          })()}
        </div>
      )}

      {/* ── Application Narrative ───────────────────────────────── */}
      {narrative.length > 0 && (
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <Sparkles size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">Application Summary</span>
            <span className="ga-dash-source-tag">derived · real data only</span>
          </div>
          <div className="ga-dash-narrative">
            {narrative.map((sentence, i) => (
              <p key={i} className="ga-dash-narrative-item">{sentence}</p>
            ))}
          </div>
        </div>
      )}

      {/* Empty state if nothing loaded */}
      {!loading && !hasGraph && !catalogData && !codeSum && (
        <div className="no-data" style={{ padding: '60px 0', textAlign: 'center' }}>
          <AlertCircle size={40} style={{ margin: '0 auto 12px', display: 'block', color: '#787c99' }} />
          <p style={{ color: '#787c99' }}>Overview data not available for this run.</p>
          <p style={{ color: '#565f89', fontSize: '12px' }}>
            Asset graph is generated when the Code Analysis node runs. Check the Statistics tab for code metrics.
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * Dependencies Tab - DOT Graph visualization
 */
function DependenciesTab({ graphData, crossReference, mainProgram, viewMode, onViewModeChange, loading, error }) {
  // Worker-mode does not produce .dot graph files — fall back to cross_reference
  const effectiveGraphData = graphData || crossReference || null

  return (
    <div className="tab-content dependencies-tab">
      <div className="tab-header">
        <h3><Network size={20} /> Dependency Graph</h3>
        <div className="view-mode-switcher">
          {Object.entries(VIEW_MODES).map(([mode, config]) => (
            <button
              key={mode}
              className={`mode-btn ${viewMode === mode ? 'active' : ''}`}
              onClick={() => onViewModeChange(mode)}
            >
              {config.label}
            </button>
          ))}
        </div>
      </div>
      <div className="graph-wrapper">
        <DependencyGraph
          graphData={effectiveGraphData}
          mainProgram={mainProgram}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          loading={loading}
          error={error}
        />
      </div>
    </div>
  )
}

/**
 * Program Flow Tab - Force-directed paragraph graph
 */
function ProgramFlowTab({ data, loading }) {
  const svgRef = useRef()
  const containerRef = useRef()

  useEffect(() => {
    if (!data?.paragraphs || !svgRef.current) return

    const container = containerRef.current
    const width = container.clientWidth || 800
    const height = container.clientHeight || 600

    // Build nodes and links from paragraphs
    const paragraphs = data.paragraphs
    const nodeMap = new Map()
    paragraphs.forEach((p, i) => nodeMap.set(p.name, i))

    const nodes = paragraphs.map(p => ({
      id: p.name,
      name: p.name,
      javaName: p.java_name,
      lineCount: p.end_line - p.start_line,
      performCount: p.statements?.filter(s => s.classification === 'PERFORM').length || 0,
    }))

    const links = []
    paragraphs.forEach(p => {
      const performs = p.statements?.filter(s => s.semantic?.type === 'PERFORM') || []
      performs.forEach(perf => {
        const target = perf.semantic?.target
        if (target && nodeMap.has(target)) {
          links.push({ source: p.name, target, thru: perf.semantic?.thru })
        }
      })
    })

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', width).attr('height', height)

    // Color by naming prefix (000-, 100-, 200-, etc.)
    const prefixes = [...new Set(nodes.map(n => n.name.split('-')[0]))]
    const color = d3.scaleOrdinal().domain(prefixes).range(COLORS.nodes)

    // Force simulation
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(80))
      .force('charge', d3.forceManyBody().strength(-200))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(25))

    // Arrow marker
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 20)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .append('path')
      .attr('d', 'M 0,-5 L 10,0 L 0,5')
      .attr('fill', '#565f89')

    // Links
    const link = svg.append('g')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', '#3b4261')
      .attr('stroke-width', 1.5)
      .attr('marker-end', 'url(#arrowhead)')

    // Nodes
    const node = svg.append('g')
      .selectAll('circle')
      .data(nodes)
      .enter()
      .append('circle')
      .attr('r', d => Math.min(8 + d.performCount * 2, 20))
      .attr('fill', d => color(d.name.split('-')[0]))
      .attr('stroke', '#1a1b26')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .call(d3.drag()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart()
          d.fx = d.x
          d.fy = d.y
        })
        .on('drag', (event, d) => {
          d.fx = event.x
          d.fy = event.y
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0)
          d.fx = null
          d.fy = null
        }))

    node.append('title').text(d => `${d.name}\nJava: ${d.javaName}\nPERFORMs: ${d.performCount}`)

    // Labels for main paragraphs
    const labels = svg.append('g')
      .selectAll('text')
      .data(nodes.filter(n => n.performCount > 0 || n.name.includes('MAIN')))
      .enter()
      .append('text')
      .attr('font-size', '9px')
      .attr('fill', '#a9b1d6')
      .attr('text-anchor', 'middle')
      .attr('dy', -15)
      .text(d => d.name.length > 15 ? d.name.substring(0, 12) + '...' : d.name)

    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y)

      node
        .attr('cx', d => d.x)
        .attr('cy', d => d.y)

      labels
        .attr('x', d => d.x)
        .attr('y', d => d.y)
    })

    return () => simulation.stop()
  }, [data])

  if (loading) return <LoadingState />

  return (
    <div className="tab-content program-flow-tab">
      <div className="tab-header">
        <h3><GitBranch size={20} /> Program Flow - Paragraph PERFORM Relationships</h3>
        <p className="tab-description">Interactive force-directed graph showing paragraph calls. Drag nodes to explore.</p>
      </div>
      <div className="chart-wrapper" ref={containerRef}>
        <svg ref={svgRef}></svg>
      </div>
    </div>
  )
}

/**
 * Copybooks Tab - Interactive ReactFlow graph
 */
function CopybooksTab({ data, copybookImpact, mainProgram, loading }) {
  if (loading) return <LoadingState />

  return (
    <div className="tab-content copybooks-tab">
      <div className="tab-header">
        <h3><Layers size={20} /> Copybook Usage Graph</h3>
        <p className="tab-description">Interactive graph showing which programs use which copybooks. Click nodes to highlight connections.</p>
      </div>
      <div className="graph-wrapper">
        <CopybookGraph
          copybookData={data}
          loading={loading}
          error={null}
        />
      </div>

      {/* Copybook Change Impact Analysis */}
      {copybookImpact?.records?.length > 0 && (
        <div className="data-section" style={{ marginTop: '24px' }}>
          <h4><AlertTriangle size={16} /> Change Impact Analysis ({copybookImpact.record_count} copybooks)</h4>
          <p style={{ color: '#a9b1d6', fontSize: '13px', marginBottom: '12px' }}>
            Impact score = programs + paragraphs affected. Higher score = more risk when modifying.
          </p>
          <div className="data-grid">
            <table>
              <thead>
                <tr>
                  <th>Copybook</th>
                  <th>Programs</th>
                  <th>Fields</th>
                  <th>Paragraphs</th>
                  <th>Impact Score</th>
                </tr>
              </thead>
              <tbody>
                {copybookImpact.records.slice(0, 20).map((r, i) => (
                  <tr key={i}>
                    <td className="copybook-name">{r.copybook}</td>
                    <td style={{ textAlign: 'center' }}>{r.program_count}</td>
                    <td style={{ textAlign: 'center' }}>{r.field_count}</td>
                    <td style={{ textAlign: 'center' }}>{r.paragraph_count}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: r.impact_score > 10 ? 'rgba(247, 118, 142, 0.15)' :
                                    r.impact_score > 5  ? 'rgba(224, 175, 104, 0.15)' :
                                                          'rgba(158, 206, 106, 0.15)',
                        color: r.impact_score > 10 ? '#f7768e' :
                               r.impact_score > 5  ? '#e0af68' : '#9ece6a',
                      }}>
                        {r.impact_score}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Data Model Tab - Field-driven report layout
 * Renders from data.fields (field-driven) when data.groups is empty.
 * Falls back gracefully when both are absent.
 */
function DataModelTab({ data, fieldXref, loading }) {
  const [xrefFilter, setXrefFilter] = useState('')

  if (loading) return <LoadingState />
  if (!data) return <div className="no-data">No data model available</div>

  const stats = data.stats || {}
  const fields = data.fields || []
  const imports = data.imports_needed || []

  // Normalise each field: inferred entries may use `name` instead of `cobol_name`
  // and have level as a string "01" rather than integer 1
  const normalizeField = (f) => ({
    ...f,
    cobol_name: f.cobol_name || f.name || '—',
    displayLevel: String(f.level).replace(/^0+/, '') || '1',
    isInferred: f.source === 'inferred' || f.line_num === -1,
  })

  const normalizedFields = fields.map(normalizeField)

  const byLevel = stats.by_level || {}
  const byJavaType = stats.by_java_type || {}

  return (
    <div className="tab-content data-model-tab">
      <div className="tab-header">
        <h3><Database size={20} /> Data Model — COBOL to Java Mapping</h3>
        <p className="tab-description">
          Field-level DATA DIVISION analysis with Java type mappings
          {data.mapping_reference ? ` · ${data.mapping_reference}` : ''}
        </p>
      </div>

      <div className="data-model-content">

        {/* ── Summary Cards ── */}
        <div className="dm-summary-cards">
          <div className="dm-card">
            <span className="dm-card-value">{stats.total_lines?.toLocaleString() ?? '—'}</span>
            <span className="dm-card-label">Total Lines</span>
          </div>
          <div className="dm-card">
            <span className="dm-card-value">{stats.data_lines?.toLocaleString() ?? '—'}</span>
            <span className="dm-card-label">Data Items</span>
          </div>
          <div className="dm-card">
            <span className={`dm-card-value ${(stats.with_occurs || 0) === 0 ? 'dm-card-value--zero' : ''}`}>
              {stats.with_occurs ?? 0}
            </span>
            <span className="dm-card-label">With OCCURS</span>
          </div>
          <div className="dm-card">
            <span className={`dm-card-value ${(stats.with_depending_on || 0) === 0 ? 'dm-card-value--zero' : ''}`}>
              {stats.with_depending_on ?? 0}
            </span>
            <span className="dm-card-label">With DEPENDING ON</span>
          </div>
          <div className="dm-card">
            <span className={`dm-card-value ${(stats.with_redefines || 0) === 0 ? 'dm-card-value--zero' : ''}`}>
              {stats.with_redefines ?? 0}
            </span>
            <span className="dm-card-label">With REDEFINES</span>
          </div>
        </div>

        {/* ── By Level + By Java Type + Imports ── */}
        <div className="dm-meta-row">
          <div className="dm-meta-section">
            <h4>By Level</h4>
            <div className="dm-badge-list">
              {Object.entries(byLevel).length > 0
                ? Object.entries(byLevel).map(([lvl, count]) => (
                    <span key={lvl} className="dm-badge dm-badge--level">
                      Level {String(lvl).padStart(2, '0')}
                      <span className="dm-badge-count">{count}</span>
                    </span>
                  ))
                : <span className="dm-empty-val">—</span>
              }
            </div>
          </div>

          <div className="dm-meta-section">
            <h4>By Java Type</h4>
            <div className="dm-badge-list">
              {Object.entries(byJavaType).length > 0
                ? Object.entries(byJavaType).map(([type, count]) => (
                    <span key={type} className={`dm-badge dm-badge--jtype dm-jtype-${type.toLowerCase().replace(/[^a-z]/g, '')}`}>
                      {type}
                      <span className="dm-badge-count">{count}</span>
                    </span>
                  ))
                : <span className="dm-empty-val">—</span>
              }
            </div>
          </div>

          {imports.length > 0 && (
            <div className="dm-meta-section">
              <h4>Java Imports</h4>
              <div className="dm-badge-list">
                {imports.map(imp => (
                  <span key={imp} className="dm-badge dm-badge--import">{imp}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Field Table ── */}
        {normalizedFields.length > 0 ? (
          <div className="data-section">
            <h4><Code2 size={16} /> Fields ({normalizedFields.length})</h4>
            <div className="data-grid dm-field-grid">
              <table>
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Lvl</th>
                    <th>COBOL Name</th>
                    <th>PIC</th>
                    <th>Java Name</th>
                    <th>Java Type</th>
                    <th>Value</th>
                    <th>Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {normalizedFields.map((field, i) => (
                    <tr key={i} className={field.isInferred ? 'dm-row-inferred' : ''}>
                      <td className="dm-cell-line">
                        {field.line_num >= 0
                          ? field.line_num
                          : <span className="dm-inferred-tag">inferred</span>
                        }
                      </td>
                      <td className="dm-cell-level">{field.displayLevel}</td>
                      <td className="dm-cell-cobol">{field.cobol_name}</td>
                      <td className="dm-cell-pic">{field.pic || '—'}</td>
                      <td className="dm-cell-jname">{field.java_name || '—'}</td>
                      <td>
                        <span className={`dm-type-badge dm-type-${(field.java_type || 'unknown').toLowerCase().replace(/[^a-z]/g, '')}`}>
                          {field.java_type || '—'}
                        </span>
                      </td>
                      <td className="dm-cell-value">
                        {field.value != null
                          ? <code className="dm-code-val">{field.value}</code>
                          : <span className="dm-null">—</span>
                        }
                      </td>
                      <td className="dm-cell-flags">
                        {field.is_group    && <span className="dm-flag">GROUP</span>}
                        {field.is_array    && <span className="dm-flag dm-flag--arr">ARRAY</span>}
                        {field.is_filler   && <span className="dm-flag dm-flag--fill">FILLER</span>}
                        {field.occurs      && <span className="dm-flag dm-flag--occ">OCCURS {field.occurs}</span>}
                        {field.redefines   && <span className="dm-flag dm-flag--red">REDEF</span>}
                        {field.isInferred  && <span className="dm-flag dm-flag--inf">INFERRED</span>}
                        {!field.is_group && !field.is_array && !field.is_filler &&
                         !field.occurs && !field.redefines && !field.isInferred &&
                          <span className="dm-null">—</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="dm-no-fields">
            <Database size={32} />
            <p>No field-level data available for this program.</p>
          </div>
        )}

        {/* ── Field Cross-Reference Search ── */}
        {fieldXref?.records?.length > 0 && (
          <div className="data-section" style={{ marginTop: '24px' }}>
            <h4><ArrowRightLeft size={16} /> Field Cross-Reference ({fieldXref.record_count} fields)</h4>
            <div style={{ marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="Filter fields by name, copybook, or PIC..."
                value={xrefFilter}
                onChange={e => setXrefFilter(e.target.value)}
                style={{
                  width: '100%', maxWidth: '400px',
                  padding: '8px 12px', borderRadius: '6px',
                  border: '1px solid #3b3d56', background: '#1e1f2e',
                  color: '#c0caf5', fontSize: '13px',
                  outline: 'none',
                }}
              />
            </div>
            <div className="data-grid">
              <table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>PIC</th>
                    <th>Java Type</th>
                    <th>Copybook</th>
                    <th>Usage Count</th>
                    <th>Used In</th>
                  </tr>
                </thead>
                <tbody>
                  {fieldXref.records
                    .filter(r => {
                      if (!xrefFilter) return true
                      const q = xrefFilter.toLowerCase()
                      return (r.field || '').toLowerCase().includes(q) ||
                             (r.cobol_name || '').toLowerCase().includes(q) ||
                             (r.pic || '').toLowerCase().includes(q) ||
                             (r.copybook || '').toLowerCase().includes(q)
                    })
                    .slice(0, 50)
                    .map((r, i) => (
                      <tr key={i}>
                        <td className="copybook-name">{r.cobol_name || r.field}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{r.pic || '—'}</td>
                        <td>
                          <span className={`dm-type-badge dm-type-${(r.java_type || 'unknown').toLowerCase().replace(/[^a-z]/g, '')}`}>
                            {r.java_type || '—'}
                          </span>
                        </td>
                        <td>{r.copybook || '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600,
                            background: r.usage_count === 0 ? 'rgba(247, 118, 142, 0.15)' : 'rgba(122, 162, 247, 0.15)',
                            color: r.usage_count === 0 ? '#f7768e' : '#7aa2f7',
                          }}>
                            {r.usage_count}
                          </span>
                        </td>
                        <td style={{ fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.used_in_paragraphs?.join(', ') || '—'}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

/**
 * File I/O Tab - Data grid
 */
function FileIOTab({ data, loading }) {
  if (loading) return <LoadingState />
  if (!data) return <div className="no-data">No file model data available</div>

  const files = data.select_statements || []
  const operations = data.file_operations || {}

  return (
    <div className="tab-content file-io-tab">
      <div className="tab-header">
        <h3><FileText size={20} /> File I/O Operations</h3>
        <p className="tab-description">SELECT statements and file operations (OPEN, CLOSE, READ, WRITE)</p>
      </div>

      <div className="file-io-content">
        {/* File definitions grid */}
        <div className="data-section">
          <h4><FileCode size={16} /> File Definitions ({files.length})</h4>
          <div className="data-grid">
            <table>
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Assign To</th>
                  <th>Organization</th>
                  <th>Access Mode</th>
                  <th>Record Key</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f, i) => (
                  <tr key={i}>
                    <td className="file-name">{f.file_name}</td>
                    <td>{f.assign_to}</td>
                    <td><span className="badge org">{f.organization}</span></td>
                    <td><span className="badge access">{f.access_mode}</span></td>
                    <td>{f.record_key}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Operations summary */}
        <div className="operations-summary">
          <div className="op-card">
            <span className="op-count">{operations.open?.length || 0}</span>
            <span className="op-label">OPEN</span>
          </div>
          <div className="op-card">
            <span className="op-count">{operations.close?.length || 0}</span>
            <span className="op-label">CLOSE</span>
          </div>
          <div className="op-card">
            <span className="op-count">{operations.read?.length || 0}</span>
            <span className="op-label">READ</span>
          </div>
          <div className="op-card">
            <span className="op-count">{operations.write?.length || 0}</span>
            <span className="op-label">WRITE</span>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Statistics Tab - Code Analysis Intelligence Dashboard
 */
function StatisticsTab({ summary, lineInventory, crossRef, procModel, comprehensiveData, complexityHeatmap, loading }) {
  const gaugeRef = useRef()

  if (loading) return <LoadingState />
  if (!summary) return <div className="no-data">No summary data available</div>

  const classifications = summary.classifications || {}
  const copybooks = crossRef?.copybook_usage ? Object.keys(crossRef.copybook_usage) : []
  const copyStatements = comprehensiveData?.programs?.[Object.keys(comprehensiveData?.programs || {})[0]]?.copy_statements || []

  // Calculate Code Quality Score (0-100)
  const totalLines = lineInventory?.source_line_count || summary?.source_line_count || 0
  const codeLines = classifications.CODE || 0
  const commentLines = classifications.COMMENT || 0
  const ifStatements = classifications.IF || 0
  const performStatements = classifications.PERFORM || 0
  const gotoStatements = classifications.GOTO || 0
  const paragraphs = procModel?.paragraphs?.length || 0

  // Quality factors
  const commentRatio = totalLines > 0 ? (commentLines / totalLines) * 100 : 0
  const codeRatio = totalLines > 0 ? (codeLines / totalLines) * 100 : 0
  const complexityScore = ifStatements + performStatements + (gotoStatements * 2)
  const avgComplexity = paragraphs > 0 ? complexityScore / paragraphs : 0

  let qualityScore = 70 // Base score

  // Comment ratio bonus (+15 max)
  if (commentRatio >= 15) qualityScore += 15
  else if (commentRatio >= 10) qualityScore += 10
  else if (commentRatio >= 5) qualityScore += 5

  // Low GOTO bonus (+10 max)
  if (gotoStatements === 0) qualityScore += 10
  else if (gotoStatements < 5) qualityScore += 5

  // Complexity penalty (-25 max)
  if (avgComplexity > 50) qualityScore -= 25
  else if (avgComplexity > 30) qualityScore -= 15
  else if (avgComplexity > 20) qualityScore -= 10
  else if (avgComplexity > 10) qualityScore += 5 // Bonus for low complexity

  qualityScore = Math.max(0, Math.min(100, qualityScore))

  // Quality level
  let qualityLevel, qualityColor
  if (qualityScore >= 80) {
    qualityLevel = 'Excellent'
    qualityColor = '#9ece6a'
  } else if (qualityScore >= 60) {
    qualityLevel = 'Good'
    qualityColor = '#7aa2f7'
  } else if (qualityScore >= 40) {
    qualityLevel = 'Fair'
    qualityColor = '#e0af68'
  } else {
    qualityLevel = 'Needs Work'
    qualityColor = '#f7768e'
  }

  // Render gauge
  useEffect(() => {
    if (!gaugeRef.current) return

    const svg = d3.select(gaugeRef.current)
    svg.selectAll('*').remove()

    const width = 220
    const height = 140
    const radius = 70
    const cx = width / 2
    const cy = height - 10

    svg.attr('viewBox', `0 0 ${width} ${height}`)

    // Background arc
    const bgArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI / 2)

    svg.append('path')
      .attr('transform', `translate(${cx},${cy})`)
      .attr('d', bgArc)
      .attr('fill', '#2a2b3d')

    // Score arc with gradient
    const scoreAngle = -Math.PI / 2 + (qualityScore / 100) * Math.PI
    const scoreArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .endAngle(scoreAngle)

    // Gradient
    const defs = svg.append('defs')
    const gradient = defs.append('linearGradient')
      .attr('id', 'quality-gradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '0%')
    gradient.append('stop').attr('offset', '0%').attr('stop-color', '#f7768e')
    gradient.append('stop').attr('offset', '50%').attr('stop-color', '#e0af68')
    gradient.append('stop').attr('offset', '100%').attr('stop-color', '#9ece6a')

    // Animated score arc
    svg.append('path')
      .attr('transform', `translate(${cx},${cy})`)
      .attr('fill', 'url(#quality-gradient)')
      .transition()
      .duration(1500)
      .ease(d3.easeElasticOut.amplitude(1).period(0.4))
      .attrTween('d', () => {
        const interpolate = d3.interpolate(-Math.PI / 2, scoreAngle)
        return (t) => {
          const arc = d3.arc()
            .innerRadius(radius - 20)
            .outerRadius(radius)
            .startAngle(-Math.PI / 2)
            .endAngle(interpolate(t))
          return arc()
        }
      })

    // Score text
    svg.append('text')
      .attr('x', cx)
      .attr('y', cy - 35)
      .attr('text-anchor', 'middle')
      .attr('fill', qualityColor)
      .attr('font-size', '36px')
      .attr('font-weight', '700')
      .text('0')
      .transition()
      .duration(1500)
      .tween('text', function() {
        const i = d3.interpolateNumber(0, qualityScore)
        return function(t) {
          this.textContent = Math.round(i(t))
        }
      })

    // Quality level text
    svg.append('text')
      .attr('x', cx)
      .attr('y', cy - 8)
      .attr('text-anchor', 'middle')
      .attr('fill', qualityColor)
      .attr('font-size', '14px')
      .attr('font-weight', '600')
      .text(qualityLevel)
      .style('opacity', 0)
      .transition()
      .delay(800)
      .duration(600)
      .style('opacity', 1)
  }, [qualityScore, qualityLevel, qualityColor])

  return (
    <div className="tab-content statistics-tab">
      <div className="tab-header">
        <h3><Activity size={20} /> Code Analysis Intelligence</h3>
        <p className="tab-description">AI-powered analysis of code quality and maintainability</p>
      </div>

      <div className="stats-content">
        {/* Intelligence Grid */}
        <div className="intelligence-grid">
          {/* Code Quality Gauge */}
          <div className="insights-card quality-card">
            <div className="card-header">
              <Target size={18} />
              <h4>Code Quality Score</h4>
            </div>
            <div className="gauge-container">
              <svg ref={gaugeRef}></svg>
            </div>
            <div className="quality-factors">
              <div className="factor">
                <span className="factor-label">Comments</span>
                <span className="factor-value" style={{ color: commentRatio >= 10 ? '#9ece6a' : '#e0af68' }}>
                  {commentRatio.toFixed(1)}%
                </span>
              </div>
              <div className="factor">
                <span className="factor-label">Complexity</span>
                <span className="factor-value" style={{ color: avgComplexity < 20 ? '#9ece6a' : '#f7768e' }}>
                  {avgComplexity.toFixed(1)}
                </span>
              </div>
              <div className="factor">
                <span className="factor-label">GOTO Usage</span>
                <span className="factor-value" style={{ color: gotoStatements === 0 ? '#9ece6a' : '#f7768e' }}>
                  {gotoStatements}
                </span>
              </div>
            </div>
          </div>

          {/* Analysis Summary */}
          <div className="insights-card summary-card">
            <div className="card-header">
              <FileBarChart size={18} />
              <h4>Analysis Summary</h4>
            </div>
            <div className="summary-stats">
              <div className="summary-item">
                <div className="summary-icon" style={{ background: 'rgba(122, 162, 247, 0.1)' }}>
                  <Code2 size={20} style={{ color: '#7aa2f7' }} />
                </div>
                <div className="summary-content">
                  <span className="summary-value">{(lineInventory?.source_line_count ?? summary?.source_line_count)?.toLocaleString()}</span>
                  <span className="summary-label">Total Lines</span>
                </div>
              </div>
              <div className="summary-item">
                <div className="summary-icon" style={{ background: 'rgba(158, 206, 106, 0.1)' }}>
                  <Layers size={20} style={{ color: '#9ece6a' }} />
                </div>
                <div className="summary-content">
                  <span className="summary-value">{paragraphs}</span>
                  <span className="summary-label">Paragraphs</span>
                </div>
              </div>
              <div className="summary-item">
                <div className="summary-icon" style={{ background: 'rgba(125, 207, 255, 0.1)' }}>
                  <Database size={20} style={{ color: '#7dcfff' }} />
                </div>
                <div className="summary-content">
                  <span className="summary-value">{copybooks.length}</span>
                  <span className="summary-label">Copybooks</span>
                </div>
              </div>
              <div className="summary-item">
                <div className="summary-icon" style={{ background: 'rgba(187, 154, 247, 0.1)' }}>
                  <Box size={20} style={{ color: '#bb9af7' }} />
                </div>
                <div className="summary-content">
                  <span className="summary-value">{(summary.total_data_items ?? summary.inventory_count)?.toLocaleString()}</span>
                  <span className="summary-label">Data Items</span>
                </div>
              </div>
            </div>
          </div>

          {/* Complexity Breakdown */}
          <div className="insights-card complexity-card">
            <div className="card-header">
              <AlertTriangle size={18} />
              <h4>Complexity Metrics</h4>
            </div>
            <div className="complexity-bars">
              <div className="complexity-bar-item">
                <div className="bar-label">
                  <span>IF Statements</span>
                  <span className="bar-value">{ifStatements}</span>
                </div>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${Math.min(100, (ifStatements / totalLines) * 5000)}%`, background: '#7aa2f7' }}></div>
                </div>
              </div>
              <div className="complexity-bar-item">
                <div className="bar-label">
                  <span>PERFORM Calls</span>
                  <span className="bar-value">{performStatements}</span>
                </div>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${Math.min(100, (performStatements / totalLines) * 5000)}%`, background: '#9ece6a' }}></div>
                </div>
              </div>
              <div className="complexity-bar-item">
                <div className="bar-label">
                  <span>GOTO Statements</span>
                  <span className="bar-value">{gotoStatements}</span>
                </div>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${Math.min(100, (gotoStatements / totalLines) * 10000)}%`, background: '#f7768e' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Copybooks Detail Grid */}
        {copyStatements.length > 0 && (
          <div className="data-section">
            <h4><Layers size={16} /> Copybook Details ({copyStatements.length} COPY statements, {copyStatements.reduce((sum, cs) => sum + (cs.copybook_items || 0), 0)} total data items)</h4>
            <div className="data-grid copybooks-detail-grid">
              <table>
                <thead>
                  <tr>
                    <th>Copybook</th>
                    <th>Line</th>
                    <th>Data Items</th>
                    <th>Status</th>
                    <th>OF File</th>
                  </tr>
                </thead>
                <tbody>
                  {copyStatements
                    .sort((a, b) => (b.copybook_items || 0) - (a.copybook_items || 0))
                    .map((cs, i) => (
                      <tr key={i} className={!cs.resolved ? 'unresolved' : ''}>
                        <td className="copybook-name">{cs.copybook}</td>
                        <td className="line-num">{cs.line_num}</td>
                        <td className="item-count">
                          {cs.copybook_items > 0 && (
                            <span className="items-badge">{cs.copybook_items}</span>
                          )}
                          {cs.copybook_items === 0 && <span className="no-items">-</span>}
                        </td>
                        <td>
                          <span className={`status-badge ${cs.resolved ? 'resolved' : 'missing'}`}>
                            {cs.resolved ? 'Resolved' : 'Missing'}
                          </span>
                        </td>
                        <td className="of-file">{cs.of_file || '-'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Statement breakdown */}
        <div className="data-section">
          <h4><Code2 size={16} /> Statement Classifications</h4>
          <div className="classifications-grid">
            {Object.entries(classifications)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 20)
              .map(([type, count]) => (
                <div key={type} className="classification-item">
                  <span className="class-type">{type}</span>
                  <span className="class-count">{count.toLocaleString()}</span>
                  <div className="class-bar" style={{ width: `${Math.min(count / 500 * 100, 100)}%` }}></div>
                </div>
              ))}
          </div>
        </div>

        {/* Complexity Heatmap */}
        {complexityHeatmap?.records?.length > 0 && (
          <div className="data-section">
            <h4><Activity size={16} /> Paragraph Complexity Heatmap ({complexityHeatmap.record_count} paragraphs)</h4>
            <p style={{ color: '#a9b1d6', fontSize: '13px', marginBottom: '12px' }}>
              Complexity = statements + (verb types × 5). Higher = harder to modernize.
            </p>
            <div className="data-grid">
              <table>
                <thead>
                  <tr>
                    <th>Paragraph</th>
                    <th>Program</th>
                    <th>Stmts</th>
                    <th>Verbs</th>
                    <th>Score</th>
                    <th>Verb Types</th>
                  </tr>
                </thead>
                <tbody>
                  {complexityHeatmap.records.slice(0, 25).map((r, i) => {
                    const maxScore = complexityHeatmap.records[0]?.complexity_score || 1
                    const pct = Math.round((r.complexity_score / maxScore) * 100)
                    const barColor = pct > 75 ? '#f7768e' : pct > 40 ? '#e0af68' : '#9ece6a'
                    const verbs = [
                      r.has_call && 'CALL',
                      r.has_read && 'READ',
                      r.has_write && 'WRITE',
                      r.has_compute && 'COMPUTE',
                      r.has_evaluate && 'EVALUATE',
                      r.has_search && 'SEARCH',
                      r.has_string && 'STRING',
                    ].filter(Boolean)
                    return (
                      <tr key={i}>
                        <td className="copybook-name">{r.paragraph}</td>
                        <td>{r.program}</td>
                        <td style={{ textAlign: 'center' }}>{r.statement_count}</td>
                        <td style={{ textAlign: 'center' }}>{r.verb_complexity}</td>
                        <td style={{ minWidth: '120px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                              flex: 1, height: '8px', borderRadius: '4px',
                              background: '#2a2b3d', overflow: 'hidden',
                            }}>
                              <div style={{
                                width: `${pct}%`, height: '100%',
                                borderRadius: '4px', background: barColor,
                                transition: 'width 0.6s ease',
                              }} />
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: barColor, minWidth: '24px' }}>
                              {r.complexity_score}
                            </span>
                          </div>
                        </td>
                        <td style={{ fontSize: '11px' }}>
                          {verbs.map(v => (
                            <span key={v} style={{
                              display: 'inline-block', padding: '1px 6px', marginRight: '4px',
                              borderRadius: '3px', background: 'rgba(122, 162, 247, 0.1)',
                              color: '#7aa2f7', fontSize: '10px', fontWeight: 600,
                            }}>{v}</span>
                          ))}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Artifact Actions Panel
 * Rendered at the bottom of the side panel.
 * Bottom padding in .side-panel-scroll ensures the floating AI button never overlaps.
 */
function ArtifactActionsPanel({ caJobId, crJobId, baseName }) {
  const apiBase = window.__MODERNIZEIT_API_URL__ || getAPIBaseUrl()

  const caZipUrl = caJobId
    ? `${apiBase}/codeanalysis/${caJobId}/results/archive/generated_java.zip`
    : null

  const crReportUrl = crJobId
    ? `${apiBase}/coderefactor/${crJobId}/results/report`
    : null

  return (
    <div className="artifact-actions-panel">
      {/* CA / Generated Java */}
      <div className="artifact-section">
        <h4 className="artifact-section-title">
          <FileArchive size={14} /> Generated Java
        </h4>
        {caZipUrl ? (
          <a
            href={caZipUrl}
            download={baseName ? `${baseName}_generated_java.zip` : 'generated_java.zip'}
            className="artifact-action-btn primary"
          >
            <Download size={14} />
            Download All Java (ZIP)
          </a>
        ) : (
          <span className="artifact-unavailable">No CA job available</span>
        )}
      </div>

      {/* Code Refactor — only shown when CR has run */}
      {crJobId && (
        <div className="artifact-section">
          <h4 className="artifact-section-title">
            <Scissors size={14} /> Code Refactor
          </h4>
          <a
            href={crReportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="artifact-action-btn secondary"
          >
            <ExternalLink size={14} />
            View Refactor Report
          </a>
        </div>
      )}
    </div>
  )
}

/**
 * Summary Panel
 */
function SummaryPanel({ stats, loading }) {
  if (loading || !stats) {
    return (
      <div className="side-panel-content">
        <div className="panel-section">
          <h4><Sparkles size={16} /> Summary</h4>
          <div className="loading-placeholder">Loading...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="side-panel-content">
      <div className="panel-section">
        <h4><Sparkles size={16} /> Analysis Summary</h4>
        <div className="summary-stats">
          <div className="stat-row">
            <span className="stat-label">Total Lines</span>
            <span className="stat-value">{stats.totalLines.toLocaleString()}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Paragraphs</span>
            <span className="stat-value">{stats.paragraphs}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Copybooks</span>
            <span className="stat-value">{stats.copybooks}</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">External Calls</span>
            <span className="stat-value">{stats.externalCalls}</span>
          </div>
        </div>
      </div>

      <div className="panel-section">
        <h4><Code2 size={16} /> Top Statements</h4>
        <div className="top-statements">
          {Object.entries(stats.classifications)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
            .map(([type, count]) => (
              <div key={type} className="stmt-row">
                <span className="stmt-type">{type}</span>
                <span className="stmt-count">{count}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Loading state component
 */
function LoadingState() {
  return (
    <div className="loading-state">
      <Loader2 size={32} className="spin" />
      <span>Loading analysis data...</span>
    </div>
  )
}

/**
 * Wrapper with ReactFlowProvider
 */
function AnalyzeView(props) {
  return (
    <ReactFlowProvider>
      <AnalyzeViewInner {...props} />
    </ReactFlowProvider>
  )
}

export default AnalyzeView
